import React, { useState, useEffect } from 'react';
import { Info } from 'lucide-react';
import styles from './styles.module.css';

const BasicInfoForm = ({ testType, formData, onUpdate, onNext }) => {
    // Local state for the form - initialize from formData if exists
    const [localData, setLocalData] = useState({
        testName: formData?.basicInfo?.testName || '',
        description: formData?.basicInfo?.description || '',
        goal: formData?.basicInfo?.goal || getDefaultGoal(testType),
    });

    // Update local state when formData changes (e.g., when user comes back)
    useEffect(() => {
        if (formData?.basicInfo) {
            setLocalData({
                testName: formData.basicInfo.testName || '',
                description: formData.basicInfo.description || '',
                goal: formData.basicInfo.goal || getDefaultGoal(testType),
            });
        }
    }, [formData?.basicInfo]);

    const handleInputChange = (field, value) => {
        setLocalData(prev => ({ ...prev, [field]: value }));
    };

    const handleContinue = () => {
        // Validate
        if (!localData.testName || localData.testName.trim().length === 0) {
            return;
        }

        // Update parent formData with basicInfo
        onUpdate({
            basicInfo: {
                testName: localData.testName.trim(),
                description: localData.description.trim(),
                goal: localData.goal.trim(),
                testType: testType
            }
        });

        // Move to next step
        onNext();
    };

    const getTestSpecificTips = () => {
        switch (testType) {
            case 'pricing':
                return {
                    title: 'Tips for Pricing Tests',
                    tips: [
                        'Test 2-3 price points to find the optimal balance',
                        'Consider testing price formats: $29.99 vs $30.00',
                        'Run tests for at least 14 days for reliable results'
                    ]
                };
            case 'element':
                return {
                    title: 'Tips for Element Tests',
                    tips: [
                        'Start with button colors and call-to-action text',
                        'Test one element at a time for clear results',
                        'Consider mobile vs desktop differences'
                    ]
                };
            case 'cart':
                return {
                    title: 'Tips for Cart & Discount Tests',
                    tips: [
                        'Test urgency messages and discount displays',
                        'Consider shipping threshold messaging',
                        'Test cart abandonment reduction strategies'
                    ]
                };
            default:
                return {
                    title: 'A/B Testing Tips',
                    tips: [
                        'Keep tests focused on one key change',
                        'Run tests long enough for statistical significance',
                        'Document your hypothesis before starting'
                    ]
                };
        }
    };

    const tips = getTestSpecificTips();
    const isFormValid = localData.testName && localData.testName.trim().length > 0;

    return (
        <div className={styles.container}>
            <h2 className={styles.sectionTitle}>Test Information</h2>

            <div className={styles.formGroup}>
                <label className={styles.label}>
                    Test Name <span className={styles.required}>*</span>
                </label>
                <input
                    type="text"
                    value={localData.testName}
                    onChange={(e) => handleInputChange('testName', e.target.value)}
                    className={styles.input}
                    placeholder="Give your test a clear, descriptive name"
                />
                <p className={styles.helpText}>Give your test a clear, descriptive name</p>
            </div>

            <div className={styles.formGroup}>
                <label className={styles.label}>Description</label>
                <textarea
                    value={localData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    className={styles.textarea}
                    placeholder="Describe what you're testing and why..."
                    rows={3}
                />
                <p className={styles.helpText}>Explain the details of what you're testing</p>
            </div>

            <div className={styles.formGroup}>
                <label className={styles.label}>Test Goal</label>
                <input
                    type="text"
                    value={localData.goal}
                    onChange={(e) => handleInputChange('goal', e.target.value)}
                    className={styles.input}
                    placeholder="What specific outcome are you trying to achieve?"
                />
                <p className={styles.helpText}>What specific outcome are you trying to achieve?</p>
            </div>

            {/* Test-specific tips */}
            <div className={styles.tipCard}>
                <div className={styles.tipIcon}>
                    <Info size={20} />
                </div>
                <div className={styles.tipContent}>
                    <h4>{tips.title}</h4>
                    <ul>
                        {tips.tips.map((tip, index) => (
                            <li key={index}>{tip}</li>
                        ))}
                    </ul>
                </div>
            </div>

            {/* Navigation */}
            <div className={styles.navigation}>
                <button
                    onClick={handleContinue}
                    className={`${styles.continueButton} ${!isFormValid ? styles.disabled : ''}`}
                    disabled={!isFormValid}
                >
                    Continue to Configuration
                </button>
            </div>
        </div>
    );
};

// Helper function outside component
function getDefaultGoal(testType) {
    switch (testType) {
        case 'pricing':
            return 'Increase conversion rate by testing different pricing displays';
        case 'element':
            return 'Improve click-through rates by testing button design';
        case 'cart':
            return 'Reduce cart abandonment with better messaging';
        default:
            return '';
    }
}

export default BasicInfoForm;